import { set } from 'vue'
import moment from 'moment'

const previousQuarters = Array.from({ length: 4 }, (_, i) => {
    const quarter = moment().subtract(i + 1, 'quarter')
    const value = `${quarter.quarter()}-${quarter.year()}`
    return {
        label: value,
        value
    }
})

const state = () => ({
    retr: null,
    reclamations: [],
    status: 'idle',
    reclamationDates: null,
    dateNow: null,
    alias: '',
    filters: {
        positiveValue: {
            type: 'radio',
            value: 0,
            text: 'Kontrahenci',
            options: [
                {
                    label: 'Wszyscy',
                    value: 0
                },
                {
                    label: 'Z dodatnią wartością retr',
                    value: 1
                },
                {
                    label: 'Z wartością retr 0,00 zł',
                    value: 2
                }
            ]
        },
        locationId: {
            additionalText: 'Sklep',
            type: 'ui-virtual-list',
            value: null,
            multi: false,
            searcher: false,
            label: 'Sklep',
            moduleName: 'merchants',
            fetchAction: 'merchantsFetch',
            getter: 'retr/locationsByNetworkAlias',
            disabledLinkGenerator: true
        },
        quarter: {
            additionalText: 'Kwartał',
            type: 'ui-virtual-list',
            value: null,
            multi: false,
            searcher: false,
            label: 'Kwartał',
            options: previousQuarters
        }
    }
})

const mutations = {
    SET_FILTER(state, { filter, value }) {
        set(state.filters[filter], 'value', value)
    },
    RESET_FILTERS(state, payload) {
        set(state, 'filters', payload)
    },
    SET_STATUS(state, payload) {
        state.status = payload
    },
    SET_DATA(state, payload) {
        state.retr = payload
    },
    SET_RECLAMATION_DATES(state, payload) {
        state.reclamationDates = payload
    },
    SET_DATE_NOW(state, payload) {
        state.dateNow = payload
    },
    SET_ALIAS(state, payload) {
        state.alias = payload
    },
    ADD_RECLAMATION(state, payload) {
        state.reclamations.push(payload)
    },
    RESET_RECLAMATIONS(state, payload) {
        state.reclamations = payload || []
    }
}

const actions = {
    async loadRetr({ commit, state, rootGetters }, payload = {}) {
        try {
            commit('RESET_RECLAMATIONS')
            commit('SET_STATUS', 'loading')

            // Set payload
            const userId = rootGetters.userId
            const [quarter, year] = (payload.quarter || state.filters.quarter.value || '').split('-')
            const locationId = state.filters.locationId.value
            const positiveValue = state.filters.positiveValue.value
            const positiveValueMap = { 1: true, 2: false }

            if (!locationId) {
                // Reset data, when no location selected
                commit('SET_DATA', null)
                return
            }

            const endpoint = `/users/${userId}/retr/${locationId}`
            const { status, data } = await this.$api.get(endpoint, {
                params: {
                    positiveValue: positiveValueMap[positiveValue],
                    quarter: quarter || undefined,
                    year: year || undefined
                }
            })

            if (status !== 200 || !data?.data) {
                throw new Error(data?.message || `Error GET ${endpoint}`)
            }

            commit('SET_DATA', data.data)
        } catch (error) {
            commit('SET_DATA', null)
            console.error(error)
        } finally {
            commit('SET_STATUS', 'idle')
        }
    },

    async loadReclamationDates({ state, commit, rootGetters }) {
        try {
            const userId = rootGetters.userId
            const quarterSplit = (state.filters.quarter.value || '').split('-')

            const endpoint = `/users/${userId}/reclamation/date`
            const { status, data } = await this.$api.get(endpoint, {
                params: {
                    quarter: quarterSplit[0] || undefined,
                    year: quarterSplit[1] || undefined
                }
            })

            if (status !== 200 || !data?.data) {
                throw new Error(data?.message || `Error GET ${endpoint}`)
            }

            commit('SET_RECLAMATION_DATES', data.data)
        } catch (error) {
            commit('SET_RECLAMATION_DATES', null)
            console.error(error)
        }
    },

    setDefaultLocationId({ commit, getters }) {
        const locationId = getters.locationsByNetworkAlias[0].value
        commit('SET_FILTER', { filter: 'locationId', value: locationId })
    },

    async setDefaultQuarter({ state, commit, dispatch, getters }) {
        let quarterToStore = null

        for (const quarter of previousQuarters) {
            await dispatch('loadRetr', { quarter: quarter.value })

            if (!getters.hasData) {
                // If no data, go to next quarter
                continue
            }

            quarterToStore = quarter.value
            break
        }
        commit('SET_FILTER', { filter: 'quarter', value: quarterToStore })
    }
}

const getters = {
    retr: state => state.retr,
    hasData: state => {
        if (!state.retr) {
            return false
        }
        const { paymentTermBonus } = state.retr

        return paymentTermBonus !== null && paymentTermBonus !== undefined
    },
    filters: state => state.filters,
    reclamations: state => state.reclamations,
    status: state => state.status,
    reclamationDates: state => state.reclamationDates,
    canComplaint: state => {
        if (state.reclamationDates === null) {
            return false
        }

        const { dateFrom, dateTo } = state.reclamationDates

        if (!dateFrom || !dateTo) {
            return false
        }

        const now = moment(state.dateNow || undefined).utc()
        const from = moment(dateFrom).utc()
        const to = moment(dateTo).utc()

        return now.isBetween(from, to, 'day', '[]')
    },
    locationsByNetworkAlias: (state, __, ___, rootGetters) => {
        return rootGetters.getLocationsByNetworkAlias(state.alias)
    },
    reclamationExists: state => ({ contractor, type }) => {
        const [quarter, year] = (state.filters.quarter.value || '').split('-')

        return state.reclamations.find((el) => {
            return el.contractor === contractor &&
                +el.quarter === +quarter &&
                +el.year === +year &&
                el.type === type
        })
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}